interface TransactionItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

interface TransactionDetailsModalProps {
  onClose: () => void;
  transaction: {
    id: string;
    time: string;
    date: string;
    amount: string;
    items: number;
    cashier: string;
    customer?: string;
    salesperson?: string;
    paymentMethod: string;
    subtotal: number;
    tax: number;
    discount: number;
    total: number;
    itemsList: TransactionItem[];
  };
}

export default function TransactionDetailsModal({ onClose, transaction }: TransactionDetailsModalProps) {
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      alert('Please allow pop-ups to print the receipt');
      return;
    }

    // Generate receipt HTML
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${transaction.id}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Courier New', monospace;
              padding: 20px;
              max-width: 400px;
              margin: 0 auto;
            }
            .receipt {
              border: 2px solid #000;
              padding: 20px;
            }
            .header {
              text-align: center;
              border-bottom: 2px dashed #000;
              padding-bottom: 15px;
              margin-bottom: 15px;
            }
            .store-name {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .store-info {
              font-size: 12px;
              line-height: 1.5;
            }
            .section {
              margin-bottom: 15px;
              padding-bottom: 15px;
              border-bottom: 1px dashed #000;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              font-size: 12px;
              margin-bottom: 5px;
            }
            .label {
              font-weight: bold;
            }
            .items-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              font-size: 12px;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #000;
            }
            .item {
              margin-bottom: 10px;
              font-size: 12px;
            }
            .item-name {
              font-weight: bold;
              margin-bottom: 3px;
            }
            .item-details {
              display: flex;
              justify-content: space-between;
              color: #333;
            }
            .totals {
              margin-top: 15px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              font-size: 12px;
              margin-bottom: 5px;
            }
            .final-total {
              display: flex;
              justify-content: space-between;
              font-size: 16px;
              font-weight: bold;
              margin-top: 10px;
              padding-top: 10px;
              border-top: 2px solid #000;
            }
            .footer {
              text-align: center;
              margin-top: 15px;
              padding-top: 15px;
              border-top: 2px dashed #000;
              font-size: 11px;
            }
            @media print {
              body {
                padding: 0;
              }
              .receipt {
                border: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <div class="store-name">RETAIL STORE</div>
              <div class="store-info">
                123 Main Street<br>
                City, State 12345<br>
                Tel: (555) 123-4567
              </div>
            </div>

            <div class="section">
              <div class="info-row">
                <span class="label">Transaction ID:</span>
                <span>${transaction.id}</span>
              </div>
              <div class="info-row">
                <span class="label">Date:</span>
                <span>${transaction.date}</span>
              </div>
              <div class="info-row">
                <span class="label">Time:</span>
                <span>${transaction.time}</span>
              </div>
              <div class="info-row">
                <span class="label">Cashier:</span>
                <span>${transaction.cashier}</span>
              </div>
              <div class="info-row">
                <span class="label">Customer:</span>
                <span>${transaction.customer || 'Walk-in'}</span>
              </div>
            </div>

            <div class="section">
              <div class="items-header">
                <span>ITEM</span>
                <span>TOTAL</span>
              </div>
              ${transaction.itemsList.map(item => `
                <div class="item">
                  <div class="item-name">${item.name}</div>
                  <div class="item-details">
                    <span>${item.quantity} x $${item.unitPrice.toFixed(2)}${item.discount > 0 ? ` (-$${item.discount.toFixed(2)})` : ''}</span>
                    <span>$${item.total.toFixed(2)}</span>
                  </div>
                  <div style="font-size: 10px; color: #666;">SKU: ${item.sku}</div>
                </div>
              `).join('')}
            </div>

            <div class="totals">
              <div class="total-row">
                <span>Subtotal:</span>
                <span>$${transaction.subtotal.toFixed(2)}</span>
              </div>
              ${transaction.discount > 0 ? `
                <div class="total-row">
                  <span>Discount:</span>
                  <span>-$${transaction.discount.toFixed(2)}</span>
                </div>
              ` : ''}
              <div class="total-row">
                <span>Tax (VAT):</span>
                <span>$${transaction.tax.toFixed(2)}</span>
              </div>
              <div class="final-total">
                <span>TOTAL:</span>
                <span>$${transaction.total.toFixed(2)}</span>
              </div>
              <div class="total-row" style="margin-top: 10px;">
                <span>Payment Method:</span>
                <span>${transaction.paymentMethod}</span>
              </div>
            </div>

            <div class="footer">
              Thank you for your purchase!<br>
              Please keep this receipt for your records<br>
              <br>
              Visit us again soon!
            </div>
          </div>

          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <i className="ri-file-list-3-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">Transaction Details</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-teal-700 mb-1">Transaction ID</p>
                <p className="text-2xl font-bold text-teal-900">{transaction.id}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-teal-700 mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-teal-900">{transaction.amount}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-teal-700 mb-1">Date &amp; Time</p>
                <p className="text-sm font-semibold text-teal-900">{transaction.date} at {transaction.time}</p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Payment Method</p>
                <p className="text-sm font-semibold text-teal-900">{transaction.paymentMethod}</p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Cashier</p>
                <p className="text-sm font-semibold text-teal-900">{transaction.cashier}</p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Customer</p>
                <p className="text-sm font-semibold text-teal-900">{transaction.customer || 'Walk-in'}</p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Salesperson</p>
                <p className="text-sm font-semibold text-teal-900">{transaction.salesperson || 'Not Assigned'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-900">Items ({transaction.items})</h3>
            </div>
            
            <div className="divide-y divide-gray-200">
              {transaction.itemsList.map((item) => (
                <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-1">{item.name}</p>
                      <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm font-bold text-gray-900">${item.total.toFixed(2)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center gap-4">
                      <span>Qty: {item.quantity}</span>
                      <span>@ ${item.unitPrice.toFixed(2)}</span>
                      {item.discount > 0 && (
                        <span className="text-red-600 font-medium">-${item.discount.toFixed(2)} discount</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Payment Summary</h3>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-sm font-medium text-gray-900">${transaction.subtotal.toFixed(2)}</span>
              </div>
              
              {transaction.discount > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-red-600">Discount:</span>
                  <span className="text-sm font-medium text-red-600">-${transaction.discount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Tax (VAT):</span>
                <span className="text-sm font-medium text-gray-900">${transaction.tax.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                <span className="text-base font-bold text-gray-900">Total:</span>
                <span className="text-xl font-bold text-teal-600">${transaction.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-printer-line text-base"></i>
            Print Receipt
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
